/**
 * Login Component - Enhanced with Modern Design
 */
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Avatar,
  Fade,
  Zoom,
  keyframes,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'vi', label: 'Tiếng Việt', flag: '🇻🇳' },
];

export default function Login() {
  const navigate = useNavigate();
  const { login, loading, error, clearError } = useAuthStore();
  const { t, i18n } = useTranslation(['auth', 'common']);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [langAnchorEl, setLangAnchorEl] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    clearError();

    try {
      await login(email, password);
      navigate('/chat');
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    setLangAnchorEl(null);
  };

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const float = keyframes`
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  `;

  const glow = keyframes`
    0%, 100% { box-shadow: 0 0 20px rgba(102, 126, 234, 0.3); }
    50% { box-shadow: 0 0 40px rgba(102, 126, 234, 0.6); }
  `;

  // Demo accounts data - all use demo123 password
  const DEMO_PASSWORD = 'demo123';
  const demoAccounts = [
    { icon: '👥', label: 'HR', email: 'hr@coaris.com' },
    { icon: '📊', label: 'Sales', email: 'sales@coaris.com' },
    { icon: '👑', label: 'Admin', email: 'admin@coaris.com' },
    { icon: '🛡️', label: 'Master', email: 'master@coaris.com' },
    { icon: '🌐', label: 'Guest', email: 'guest@coaris.com' },
  ];

  const handleQuickLogin = (email) => {
    setEmail(email);
    setPassword(DEMO_PASSWORD);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden',
        py: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      {/* Language Selector - Top Right */}
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        <IconButton
          onClick={(e) => setLangAnchorEl(e.currentTarget)}
          sx={{
            color: 'white',
            backgroundColor: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(10px)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' },
          }}
        >
          <LanguageIcon />
        </IconButton>
        <Menu
          anchorEl={langAnchorEl}
          open={Boolean(langAnchorEl)}
          onClose={() => setLangAnchorEl(null)}
          PaperProps={{
            sx: { borderRadius: 2, minWidth: 160 }
          }}
        >
          {LANGUAGES.map((lang) => (
            <MenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              selected={i18n.language === lang.code}
            >
              <ListItemIcon sx={{ minWidth: 32, fontSize: '1.2rem' }}>
                {lang.flag}
              </ListItemIcon>
              <ListItemText>{lang.label}</ListItemText>
              {i18n.language === lang.code && (
                <CheckIcon sx={{ ml: 1, fontSize: 18, color: 'primary.main' }} />
              )}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* Center - Login Form */}
      <Zoom in timeout={600}>
        <Box sx={{ width: '100%', maxWidth: 420, px: 2 }}>
          {/* Logo and Title */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'white',
                color: '#667eea',
                margin: '0 auto 12px',
                animation: `${float} 3s ease-in-out infinite`,
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 36 }} />
            </Avatar>
            <Typography
              component="h1"
              variant="h4"
              sx={{
                color: 'white',
                fontWeight: 800,
                mb: 0.5,
                textShadow: '0 2px 20px rgba(0,0,0,0.2)',
              }}
            >
              {t('common:brand.chat')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 300 }}
            >
              {t('auth:tagline')}
            </Typography>
          </Box>

          {/* Login Card */}
          <Fade in timeout={800}>
            <Card
              sx={{
                backdropFilter: 'blur(20px)',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: 4,
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box component="form" onSubmit={handleSubmit}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label={t('auth:email')}
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label={t('auth:password')}
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />

                  {error && (
                    <Fade in>
                      <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                        {error}
                      </Alert>
                    </Fade>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      mt: 3,
                      mb: 1,
                      py: 1.2,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
                      animation: loading ? `${glow} 2s ease-in-out infinite` : 'none',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 24px rgba(102, 126, 234, 0.5)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {loading ? t('auth:loading') : t('auth:submit')}
                  </Button>

                  {/* Language indicator */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {currentLang.flag} {currentLang.label}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Fade>

          {/* Demo Accounts - Below Login Card */}
          <Fade in timeout={1000}>
            <Box sx={{ mt: 2 }}>
              <Card
                sx={{
                  backdropFilter: 'blur(20px)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.15)',
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      color: 'white',
                      display: 'block',
                      mb: 1.5,
                      fontSize: '0.75rem',
                      textAlign: 'center'
                    }}
                  >
                    <AutoAwesomeIcon sx={{ mr: 0.5, fontSize: 14, verticalAlign: 'middle' }} />
                    {t('auth:demo.title')} (demo123)
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    flexWrap: 'wrap',
                    gap: 1,
                    justifyContent: 'center'
                  }}>
                    {demoAccounts.map((acc, idx) => (
                      <Chip
                        key={idx}
                        icon={<span style={{ fontSize: '1rem' }}>{acc.icon}</span>}
                        label={acc.email}
                        onClick={() => handleQuickLogin(acc.email)}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(255,255,255,0.15)',
                          color: 'white',
                          cursor: 'pointer',
                          flex: { xs: '1 1 100%', sm: '0 1 calc(50% - 8px)' },
                          justifyContent: 'flex-start',
                          height: 32,
                          '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.3)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                          },
                          '& .MuiChip-label': { fontSize: '0.7rem', pl: 0.5, fontWeight: 500 },
                          '& .MuiChip-icon': { ml: 0.5 },
                          transition: 'all 0.2s ease',
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Fade>

          {/* Footer */}
          <Typography
            variant="caption"
            sx={{
              textAlign: 'center',
              mt: 3,
              display: 'block',
              color: 'rgba(255,255,255,0.7)',
              fontSize: '0.7rem',
            }}
          >
            {t('common:footer.copyright')}
          </Typography>
        </Box>
      </Zoom>
    </Box>
  );
}

